import { useEffect, useState } from "react";
import { formatDuration } from "./ReviewProgressDetail";
import { useI18n } from "../i18n";

export interface PendingSubmissionShape {
  tempId: string;
  title: string;
  startedAt: number;
  uploadPercent?: number;
}

/**
 * Renders the client-only placeholder shown for a review submission before the
 * server has created the review (and thus before any `ReviewProgressItem`
 * exists). Ticks its own elapsed-time clock since there's no server data to
 * poll yet — the file upload itself can take a while, so this keeps the wait
 * visibly alive instead of showing a static sentence the whole time.
 */
export function PendingReviewProgress({ pending }: { pending: PendingSubmissionShape }) {
  const { t } = useI18n();
  const [, forceTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => forceTick((n) => n + 1), 1_000);
    return () => window.clearInterval(id);
  }, []);

  const percent = pending.uploadPercent;
  const isProcessing = percent === 100;
  const elapsed = formatDuration(Date.now() - pending.startedAt);

  return (
    <div className="min-w-0 flex-1">
      <p className="mb-1 truncate text-sm font-semibold text-slate-900">{pending.title}</p>
      <p className="text-sm text-slate-600">
        {percent === undefined
          ? t("analysisProgressUploading")
          : isProcessing
            ? t("analysisProgressProcessingUpload")
            : t("analysisProgressUploadingPercent", { percent })}
      </p>
      {percent !== undefined && (
        <div
          className="mt-2 h-1 overflow-hidden rounded-full bg-slate-100"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`h-full rounded-full bg-brand-400 transition-[width] duration-500 ${
              isProcessing ? "animate-pulse" : ""
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-500">{t("analysisProgressElapsed", { time: elapsed })}</p>
    </div>
  );
}
